import type { components } from '@heidi/schema/ml-scribe';
import {
  LetterContentType,
  LetterDeliveryStatus,
  LetterEditableStatus,
  LetterElectronicallySignedSignatureType,
  LetterEmailRecipientType,
  LetterSendRecipientDeliveryStatus,
  LetterSendRecipientRole,
} from '@heidi/schema/ml-scribe';
import type { AxiosResponse } from 'axios';
import { getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

type LetterResponse = components['schemas']['LetterResponse'];
type ListLettersResponse = components['schemas']['LetterListResponse'];

type DebugRecipientResult = NonNullable<
  NonNullable<LetterResponse['delivery_details']>['recipient_results']
>[number];

export const LETTER_SENDER_NOTE_PANEL_DEBUGGER_STORAGE_KEY =
  'letter-sender-v2-note-panel-debugger-view';

export const letterSenderNotePanelDebuggerModes = [
  'draft',
  'sent',
  'partially-sent',
  'failed',
] as const;

export type LetterSenderNotePanelDebuggerMode =
  (typeof letterSenderNotePanelDebuggerModes)[number];

type DebugModeWithPayload = LetterSenderNotePanelDebuggerMode;

export const isLetterSenderNotePanelDebuggerMode = (
  value: string | null
): value is LetterSenderNotePanelDebuggerMode => {
  return letterSenderNotePanelDebuggerModes.some((mode) => mode === value);
};

export const letterSenderNotePanelDebuggerModeAtom = atomWithStorage<
  LetterSenderNotePanelDebuggerMode | undefined
>(LETTER_SENDER_NOTE_PANEL_DEBUGGER_STORAGE_KEY, undefined);

export const letterSenderNotePanelDebuggerLetterIdAtom = atomWithStorage<string | null>(
  'letter-sender-v2-note-panel-debugger-letter-id',
  null
);

const store = getDefaultStore();

export const getLetterSenderNotePanelDebuggerMode = () => {
  return store.get(letterSenderNotePanelDebuggerModeAtom);
};

type WindowWithLetterDebugger = Window & {
  toggleLetterDebugger?: () => void;
};

if (typeof window !== 'undefined') {
  (window as WindowWithLetterDebugger).toggleLetterDebugger = () => {
    store.set(letterSenderNotePanelDebuggerModeAtom, (mode) =>
      isLetterSenderNotePanelDebuggerMode(mode ?? null) ? undefined : 'draft'
    );
  };
}

const now = '2026-06-23T10:30:00.000Z';

const primaryRecipient: NonNullable<LetterResponse['primary_recipient']> = {
  type: LetterEmailRecipientType.email,
  recipient: {
    email_address: 'debug-recipient@example.com',
  },
};

const copyRecipient: NonNullable<LetterResponse['primary_recipient']> = {
  type: LetterEmailRecipientType.email,
  recipient: {
    email_address: 'debug-cc@example.com',
  },
};

const debugSignature: NonNullable<LetterResponse['signature']> = {
  type: LetterElectronicallySignedSignatureType.electronically_signed,
  properties: {
    signed_at: now,
    signed_by: {
      user_id: 'debug-user',
      first_name: 'Debug',
      last_name: 'Doctor',
    },
    applied_by: {
      user_id: 'debug-user',
      first_name: 'Debug',
      last_name: 'Doctor',
    },
  },
};

const createBaseDebugLetter = ({
  sessionId,
  letterId,
}: {
  sessionId: string;
  letterId: string;
}): LetterResponse => ({
  id: letterId,
  session_id: sessionId,
  source_document_id: 'debug-document',
  name: 'Debug letter',
  letterhead_id: 'debug-letterhead',
  body_content:
    '<p>Dear Debug Recipient,</p><p>This is sample letter body content for testing the letter sender note panel.</p><p>Kind regards,<br />Debug Doctor</p>',
  subject_line: 'Debug letter subject',
  message_body: 'This is a sample message body attached to the debug letter.',
  content_type: LetterContentType.HTML,
  content_type_version: 1,
  primary_recipient: primaryRecipient,
  copy_recipients: [copyRecipient],
  editable_status: LetterEditableStatus.FREE,
  delivery_status: null,
  delivery_details: null,
  signature: null,
  created_by: 'debug-user',
  created_at: now,
  updated_at: now,
});

const sentRecipientResults: DebugRecipientResult[] = [
  primaryRecipient,
  copyRecipient,
].map((recipient, index) => ({
  recipient,
  role: index === 0 ? LetterSendRecipientRole.PRIMARY : LetterSendRecipientRole.COPY,
  delivery_status: LetterSendRecipientDeliveryStatus.SENT,
}));

export const createDebugLetterPayload = ({
  sessionId,
  letterId,
}: {
  sessionId: string;
  letterId: string;
}): Record<DebugModeWithPayload, ListLettersResponse> => {
  const baseDebugLetter = createBaseDebugLetter({ sessionId, letterId });

  return {
    draft: {
      letters: [baseDebugLetter],
    },
    sent: {
      letters: [
        {
          ...baseDebugLetter,
          editable_status: LetterEditableStatus.LOCKED,
          delivery_status: LetterDeliveryStatus.SENT,
          delivery_details: {
            last_attempted_at: now,
            recipient_results: sentRecipientResults,
          },
          signature: debugSignature,
        },
      ],
    },
    'partially-sent': {
      letters: [
        {
          ...baseDebugLetter,
          editable_status: LetterEditableStatus.LOCKED,
          delivery_status: LetterDeliveryStatus.PARTIALLY_SENT,
          delivery_details: {
            last_attempted_at: now,
            recipient_results: [
              sentRecipientResults[0]!,
              {
                recipient: copyRecipient,
                role: LetterSendRecipientRole.COPY,
                delivery_status: LetterSendRecipientDeliveryStatus.FAILED,
              },
            ],
          },
          signature: debugSignature,
        },
      ],
    },
    failed: {
      letters: [
        {
          ...baseDebugLetter,
          editable_status: LetterEditableStatus.FREE,
          delivery_status: LetterDeliveryStatus.FAILED,
          delivery_details: {
            last_attempted_at: now,
            recipient_results: [
              {
                recipient: primaryRecipient,
                role: LetterSendRecipientRole.PRIMARY,
                delivery_status: LetterSendRecipientDeliveryStatus.FAILED,
              },
              {
                recipient: copyRecipient,
                role: LetterSendRecipientRole.COPY,
                delivery_status: LetterSendRecipientDeliveryStatus.FAILED,
              },
            ],
          },
          signature: debugSignature,
        },
      ],
    },
  };
};

export const getDebugLetterPayload = (
  sessionId: string
): AxiosResponse<ListLettersResponse> | null => {
  const mode = getLetterSenderNotePanelDebuggerMode() ?? null;

  if (!isLetterSenderNotePanelDebuggerMode(mode)) return null;

  const debugMode: DebugModeWithPayload = mode;
  const letterId = store.get(letterSenderNotePanelDebuggerLetterIdAtom) ?? 'debug-letter';
  const debugLetterPayload = createDebugLetterPayload({ sessionId, letterId });

  return {
    data: debugLetterPayload[debugMode],
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {} as AxiosResponse<ListLettersResponse>['config'],
  };
};
